/**
 * RiderDeductionFilterSection Component
 * 수수료 관리 검색 필터 섹션
 */
import React, { useMemo } from 'react';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { STATUS_FILTER_OPTIONS } from "../utils/feeManagementHelpers";
import '@/shared/styles/search-form.css';

// 필터 기본값
const DEFAULT_FILTER = {
    ALL: '전체'
} as const;

interface FilterSectionProps {
    /** 선택된 협력사 */
    partnerId: string;
    /** 협력사명 목록 */
    supplierNames: string[];
    /** 선택된 상태 */
    statusFilter: string;
    /** 협력사 변경 핸들러 */
    onPartnerChange: (value: string) => void;
    /** 상태 변경 핸들러 */
    onStatusChange: (value: string) => void;
    /** 검색 실행 핸들러 */
    onSearch: () => void;
    /** 빠른 필터 핸들러 (버튼 클릭 시 즉시 필터링) */
    onQuickFilter: (status: string) => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
                                                         partnerId,
                                                         supplierNames,
                                                         statusFilter,
                                                         onPartnerChange,
                                                         onStatusChange,
                                                         onSearch,
                                                         onQuickFilter
                                                     }) => {
    // 협력사 옵션을 SelectOption 형식으로 변환
    const supplierOptions: SelectOption[] = useMemo(() => {
        return supplierNames.map((name) => ({
            value: name,
            label: name
        }));
    }, [supplierNames]);

    const activeStatusLabel = STATUS_FILTER_OPTIONS.find(o => o.value === statusFilter && o.value !== '')?.label;
    const activePartnerLabel = partnerId !== DEFAULT_FILTER.ALL ? partnerId : null;
    const hasActiveFilter = !!activeStatusLabel || !!activePartnerLabel;

    return (
        <div className="card search-card">
            <div className="card-body">
                {/* 빠른 필터 버튼 */}
                <div className="quick-filter-row">
                    <span style={{
                        fontSize: '0.78rem', fontWeight: '600', color: '#6b7280',
                        marginRight: '8px', whiteSpace: 'nowrap', alignSelf: 'center'
                    }}>
                        협력사 상태
                    </span>
                    <div className="quick-filter-buttons">
                        {STATUS_FILTER_OPTIONS.map(option => (
                            <button
                                key={option.value}
                                type="button"
                                className={`quick-filter-btn ${statusFilter === option.value ? 'active' : ''}`}
                                onClick={() => onQuickFilter(option.value)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="search-form fee-search-form">
                    {/* 협력사 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-building" />
                                협력사
                            </label>
                            <SearchableSelect
                                options={supplierOptions}
                                value={partnerId === DEFAULT_FILTER.ALL ? '' : partnerId}
                                onChange={(value) => onPartnerChange(value || DEFAULT_FILTER.ALL)}
                                placeholder="협력사 검색..."
                                showAllOption={true}
                                allOptionLabel={DEFAULT_FILTER.ALL}
                            />
                        </div>
                    </div>

                    {/* 버튼 그룹 */}
                    <div className="form-group button-group">
                        <button className="btn btn-primary" onClick={onSearch}>
                            <i className="fas fa-search" />
                            조회
                        </button>
                    </div>
                </div>

                {/* 적용 중인 필터 칩 */}
                {hasActiveFilter && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600' }}>적용 중:</span>
                        {activeStatusLabel && (
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                fontSize: '0.75rem', color: '#1d4ed8', fontWeight: '600',
                                backgroundColor: '#dbeafe', padding: '2px 8px', borderRadius: '12px',
                            }}>
                                <i className="fas fa-filter" style={{ fontSize: '0.65rem' }}></i>
                                {activeStatusLabel}
                                <button
                                    type="button"
                                    onClick={() => onQuickFilter('')}
                                    style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        padding: '0', color: '#1d4ed8', fontSize: '0.7rem', lineHeight: 1,
                                    }}
                                    title="상태 필터 초기화"
                                >
                                    ×
                                </button>
                            </span>
                        )}
                        {activePartnerLabel && (
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                fontSize: '0.75rem', color: '#6d28d9', fontWeight: '600',
                                backgroundColor: '#ede9fe', padding: '2px 8px', borderRadius: '12px',
                            }}>
                                <i className="fas fa-building" style={{ fontSize: '0.65rem' }}></i>
                                {activePartnerLabel}
                                <button
                                    type="button"
                                    onClick={() => onPartnerChange(DEFAULT_FILTER.ALL)}
                                    style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        padding: '0', color: '#6d28d9', fontSize: '0.7rem', lineHeight: 1,
                                    }}
                                    title="협력사 필터 초기화"
                                >
                                    ×
                                </button>
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FilterSection;
